import * as ROUTES from 'constants/routes'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material'
import { ChangeEvent, DragEvent, useMemo, useRef, useState } from 'react'
import { useQuery } from 'react-query'
import { generatePath, useHistory } from 'react-router-dom'
import { getRuns, GetRunsParams } from 'utils/api'
import { ConflictMode } from 'redux/slices/LabelingSlice'
import useLabelingSlice from 'redux/slices/hooks/useLabelingSlice'
import StartAnalysisDlg from '../startAnalysis/StartAnalysisDlg'
import useSessionApi from '../useSessionApi'
import { LabelExportButton } from './LabelExportButton'
import { LabelingDirtyPrompt } from './LabelingDirtyPrompt'
import { LabelingGrid } from './LabelingGrid'
import { LabelSelector } from './LabelSelector'
import { FALLBACK_LABEL_COLOR, useLabelColors } from './labelColors'
import { loadLabeling } from './labelingStorage'
import { parseLabelFile } from './labelingFileUtils'
import { useLabelingPersistence } from './useLabelingPersistence'

const FILTER_ALL = 'all'
const FILTER_UNLABELED = 'unlabeled'
const LABEL_FILTER_PREFIX = 'lbl:'

interface Entry {
  cellId: string
  label: string
  labeledAt: string
}

interface ParsedFile {
  runId: string
  fileName: string
  entries: Entry[]
}

const ACCEPT = '.tsv,.csv,text/tab-separated-values,text/csv'

/** Recovers the run id from a labels_<runId>_<date>.tsv filename, if present. */
const runIdFromFileName = (fileName: string): string => {
  const match = fileName.match(/^labels_(.+)_\d{4}-\d{2}-\d{2}\./)
  return match ? match[1] : ''
}

/**
 * Session-less "vet labels from a TSV" page. Drop a classifier label file and
 * its cells render for review (images fetched by cell_id, no session needed).
 * Relabel, remove, and export. Internal-only (gated by the route).
 */
export const QuickVetPage = (): JSX.Element => {
  const {
    labeling: { selectedCellIds, labels, sessionLabels },
    invertSelection,
    clearSelection,
    removeLabelFromSelection,
    importLabels,
    hydrate,
  } = useLabelingSlice()
  const labelColors = useLabelColors()
  const history = useHistory()
  const { getSessions } = useSessionApi()

  const [runId, setRunId] = useState<string | undefined>(undefined)
  const [cellIds, setCellIds] = useState<string[]>([])
  const [labelFilter, setLabelFilter] = useState<string>(FILTER_ALL)
  const [error, setError] = useState<string | undefined>(undefined)
  const [conflictPrompt, setConflictPrompt] = useState<ParsedFile | null>(null)
  const [opening, setOpening] = useState(false)
  const [showAnalysis, setShowAnalysis] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // The run behind the current file, needed to seed the Start Analysis dialog
  // (cell counts, run id) when no ready session exists yet.
  const { data: runsForAnalysis } = useQuery(
    ['getRuns', { run_ids: runId ? [runId] : [] }] as [string, GetRunsParams],
    getRuns,
    { enabled: !!runId }
  )

  // Persists the labels for this run (save-only here: the scope and labels are
  // established synchronously by applyImport below, so the load path is a no-op).
  useLabelingPersistence(runId)

  // Commit a parsed file's labels under its run, using the chosen merge mode.
  // Scope and labels are set atomically (hydrate then merge) in a single
  // synchronous dispatch sequence, so the labels can never be lost to a race
  // with the persistence hook or an interrupted render.
  const applyImport = (parsed: ParsedFile, mode: ConflictMode) => {
    const existing = loadLabeling(parsed.runId)
    // Enter the run scope, restoring any previously saved labels for it.
    hydrate({
      labels: existing?.labels ?? {},
      labeledAt: existing?.labeledAt ?? {},
      sessionLabels: existing?.sessionLabels ?? [],
      activeLabel: existing?.activeLabel,
      dirty: existing?.dirty ?? false,
      scopeKey: parsed.runId,
    })
    // Merge the file's labels on top of the restored state.
    importLabels({ entries: parsed.entries, mode })
    setCellIds(parsed.entries.map((entry) => entry.cellId))
    setLabelFilter(FILTER_ALL)
    setRunId(parsed.runId)
    setConflictPrompt(null)
  }

  // Escalate to the full session view: find a ready Cell Vis session for this
  // run and open its labeling page. Labels are keyed by run, so any edits made
  // while vetting follow into the session (and vice versa).
  const openInSession = async () => {
    if (!runId) return
    setOpening(true)
    try {
      const sessions = await getSessions({ run_ids: runId })
      const ready = sessions.filter((session) => session.status === 'ready')
      const target =
        ready.find((session) => session.is_latest) ??
        [...ready].sort((a, b) => b.session_id - a.session_id)[0]
      if (target) {
        history.push(
          generatePath(ROUTES.CELL_VISUALIZATIONS_SESSION_LABEL, {
            sessionId: String(target.session_id),
          })
        )
        return
      }
      // No ready session for this run: open Start Analysis prefilled with this
      // run so the user can create one (the dialog navigates on completion).
      setShowAnalysis(true)
    } finally {
      setOpening(false)
    }
  }

  const loadFile = async (file: File) => {
    setError(undefined)
    try {
      const rows = parseLabelFile(await file.text())
      const parsedRunId = rows.find((row) => row.run_id)?.run_id || runIdFromFileName(file.name)
      if (!parsedRunId) {
        setError('Could not determine the run id (no run_id column and none in the filename).')
        return
      }
      const parsed: ParsedFile = {
        runId: parsedRunId,
        fileName: file.name,
        entries: rows.map((row) => ({
          cellId: row.cell_id,
          label: row.label,
          labeledAt: row.labeled_at,
        })),
      }
      // If this run already has labels (from an earlier vet or the session flow),
      // ask how to merge instead of silently clobbering existing edits.
      const existing = loadLabeling(parsedRunId)
      if (existing && Object.keys(existing.labels).length > 0) {
        setConflictPrompt(parsed)
      } else {
        applyImport(parsed, 'imported-wins')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not read the file.')
    }
  }

  const conflictDialog = (
    <Dialog open={Boolean(conflictPrompt)} onClose={() => setConflictPrompt(null)}>
      <DialogTitle>This run already has labels</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {conflictPrompt
            ? `"${conflictPrompt.fileName}" is for a run you have already labeled. How should the imported labels be merged?`
            : ''}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setConflictPrompt(null)}>Cancel</Button>
        <Button
          onClick={() => conflictPrompt && applyImport(conflictPrompt, 'current-wins')}
          data-testid="vet-keep-current"
        >
          Keep current labels on conflict
        </Button>
        <Button
          variant="contained"
          onClick={() => conflictPrompt && applyImport(conflictPrompt, 'imported-wins')}
          data-testid="vet-imported-wins"
        >
          Imported labels override on conflict
        </Button>
      </DialogActions>
    </Dialog>
  )

  const onFileInput = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) loadFile(file)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    const file = event.dataTransfer.files?.[0]
    if (file) loadFile(file)
  }

  const visibleCellIds = useMemo(() => {
    if (labelFilter === FILTER_ALL) return cellIds
    if (labelFilter === FILTER_UNLABELED) return cellIds.filter((id) => !labels[id])
    if (labelFilter.startsWith(LABEL_FILTER_PREFIX)) {
      const target = labelFilter.slice(LABEL_FILTER_PREFIX.length)
      return cellIds.filter((id) => labels[id] === target)
    }
    return cellIds
  }, [labelFilter, cellIds, labels])

  const totalSelected = Object.keys(selectedCellIds).length
  const totalLabeled = useMemo(() => cellIds.filter((id) => labels[id]).length, [cellIds, labels])

  if (!runId) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant="h6" sx={{ mb: 0.5 }}>
          Vet labels from a file
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
          Drop a classifier label TSV/CSV to review its cells. No session needed.
        </Typography>
        <Box
          onDrop={onDrop}
          onDragOver={(event) => event.preventDefault()}
          onClick={() => fileInputRef.current?.click()}
          sx={{
            border: '2px dashed #c4c0e8',
            borderRadius: 2,
            p: 6,
            textAlign: 'center',
            cursor: 'pointer',
            backgroundColor: '#faf9ff',
          }}
          data-testid="vet-dropzone"
        >
          <Typography variant="body1">Drag a labels file here, or click to choose</Typography>
        </Box>
        <input ref={fileInputRef} type="file" accept={ACCEPT} hidden onChange={onFileInput} />
        {error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        ) : null}
        {conflictDialog}
      </Box>
    )
  }

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        sx={{ px: 2, py: 1.5, borderBottom: '1px solid #ececf3' }}
      >
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Run:
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {runId}
        </Typography>
        <Button size="small" variant="text" onClick={() => fileInputRef.current?.click()}>
          Load another
        </Button>
        <input ref={fileInputRef} type="file" accept={ACCEPT} hidden onChange={onFileInput} />
        <Button
          size="small"
          variant="text"
          onClick={openInSession}
          disabled={opening}
          startIcon={opening ? <CircularProgress size={14} /> : undefined}
          data-testid="vet-open-in-session"
        >
          Open in session
        </Button>

        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Show:
        </Typography>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <Select
            value={labelFilter}
            onChange={(event) => setLabelFilter(event.target.value)}
            renderValue={(value) => {
              if (value === FILTER_ALL) return 'All cells'
              if (value === FILTER_UNLABELED) return 'Unlabeled'
              return value.startsWith(LABEL_FILTER_PREFIX)
                ? value.slice(LABEL_FILTER_PREFIX.length)
                : String(value)
            }}
            data-testid="vet-filter"
          >
            <MenuItem value={FILTER_ALL}>All cells</MenuItem>
            <MenuItem value={FILTER_UNLABELED}>Unlabeled</MenuItem>
            {sessionLabels.map((label) => (
              <MenuItem key={label} value={`${LABEL_FILTER_PREFIX}${label}`}>
                <Stack direction="row" alignItems="center" gap={1}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '3px',
                      backgroundColor: labelColors[label] ?? FALLBACK_LABEL_COLOR,
                      flex: 'none',
                    }}
                  />
                  <span>{label}</span>
                </Stack>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <LabelSelector />
        <Button
          size="small"
          variant="outlined"
          onClick={() => removeLabelFromSelection()}
          disabled={totalSelected === 0}
        >
          Remove label
        </Button>
        <Typography
          variant="body2"
          sx={{ color: 'primary.main', fontWeight: 600, letterSpacing: '0.06em' }}
          data-testid="vet-selected-count"
        >
          {totalSelected.toLocaleString()} SELECTED
        </Typography>
        <Button
          size="small"
          variant="outlined"
          onClick={() => invertSelection({ cellIds: visibleCellIds, includeLabeled: true })}
          disabled={visibleCellIds.length === 0}
        >
          Invert Selections
        </Button>
        <Button
          size="small"
          variant="text"
          onClick={() => clearSelection()}
          disabled={totalSelected === 0}
        >
          Clear
        </Button>
        <Box sx={{ flex: 1 }} />
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {totalLabeled.toLocaleString()} labeled · {cellIds.length.toLocaleString()} in file
        </Typography>
        <LabelExportButton runId={runId} />
      </Stack>

      <Box sx={{ p: 2, height: 'calc(100vh - 200px)', overflow: 'auto' }}>
        <LabelingGrid cellIds={visibleCellIds} selectLabeled />
      </Box>
      {/* Escalating into a session keeps the labels (persisted by run), so do not
          warn about "unsaved" labels when navigating to an existing session's
          /label view or into a newly created session (/prefilter). */}
      <LabelingDirtyPrompt ignorePaths={['/label', '/prefilter']} />
      {conflictDialog}
      <StartAnalysisDlg
        isOpen={showAnalysis && (runsForAnalysis?.length ?? 0) > 0}
        runs={runsForAnalysis ?? []}
        handleClose={() => setShowAnalysis(false)}
        hideMorphometricsModel
      />
    </Box>
  )
}

export default QuickVetPage
